// Payments/BalanceDisplay.js

import React, { useState, useEffect } from 'react';
import axiosInstance from "../../utils/axiosInstance";
import { hasPermission } from '../../utils/permissionUtils';

import {
  Box, Typography, Button, CircularProgress, Modal, Alert, TextField,
  Collapse, IconButton, FormControl, InputLabel, Select, MenuItem, Grid,
  Card, CardContent, Chip, List, ListItem, ListItemText, Divider, Paper,
  Stack, alpha, useTheme
} from '@mui/material';
import AddLinkIcon from '@mui/icons-material/AddLink';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const BalanceDisplay = ({ onWithdrawalSuccess, refreshKey }) => {
    const theme = useTheme();
    const [balance, setBalance] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAccountReady, setIsAccountReady] = useState(false);
    const [error, setError] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [withdrawalAmount, setWithdrawalAmount] = useState('');

    const [isLinkFormOpen, setLinkFormOpen] = useState(false);
    const [linkRequest, setLinkRequest] = useState({
        amount: '',
        title: '',
        description: '',
        payment_type: 'others'
    });
    const [planRatios, setPlanRatios] = useState({
        all_plan_ratios: [],
        current_user_plan: null
    });
    const [isRatiosLoading, setIsRatiosLoading] = useState(true);

    const [generatedLink, setGeneratedLink] = useState('');
    const [isGeneratingLink, setIsGeneratingLink] = useState(false);
    const [linkError, setLinkError] = useState('');

    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                const viewPerm = await hasPermission('payments', 'view');
                const modifyPerm = await hasPermission('payments', 'add|edit');
                setCanView(viewPerm);
                setCanModify(modifyPerm);
            } catch (error) {
                console.error('Error checking permissions:', error);
                setCanView(false);
                setCanModify(false);
            } finally {
                setCheckingPermissions(false);
            }
        };

        checkPermissions();
    }, []);

    useEffect(() => {
        setIsLoading(true);
        axiosInstance.get(`/payments/balance`)
            .then(res => {
                setBalance(res.data.balance);
                setIsAccountReady(res.data.is_stripe_account_ready);
            })
            .catch(err => setError('Failed to load balance.'))
            .finally(() => setIsLoading(false));

        setIsRatiosLoading(true);
        axiosInstance.get(`/payments/plan-ratios`)
            .then(res => {
                setPlanRatios(res.data.data);
            })
            .catch(err => { if (!axiosInstance.isCancel(err)) console.error("Failed to fetch plan ratios", err); })
            .finally(() => setIsRatiosLoading(false));
    
    }, [refreshKey]);

    const handleConnectStripe = async () => {
        setIsProcessing(true);
        try {
            const res = await axiosInstance.post(`/payments/create-connect-link`, {
                return_url: window.location.href,
                refresh_url: window.location.href,
            });
            window.location.href = res.data.url;
        } catch (err) {
            setError(err.response?.data?.message || 'Could not create Stripe connection link.');
            setIsProcessing(false);
        }
    };
    
    const handleWithdraw = async () => {
        setIsProcessing(true);
        setError('');
        try {
            await axiosInstance.post(`/payments/initiate-withdrawal`, { amount: withdrawalAmount });
            setOpenModal(false);
            onWithdrawalSuccess();
        } catch (err) {
            setError(err.response?.data?.message || 'Withdrawal failed.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleOpenModal = () => {
        setWithdrawalAmount(balance);
        setError('');
        setOpenModal(true);
    };

    const handleLinkRequestChange = (e) => {
        const { name, value } = e.target;
        setLinkRequest(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleGenerateLink = async (e) => {
        e.preventDefault();
        setIsGeneratingLink(true);
        setGeneratedLink('');
        setLinkError('');
        try {
            const res = await axiosInstance.post(`/payments/generate-specific-payment-token`, linkRequest);
            setGeneratedLink(`${window.location.origin}/pay/${res.data.token}`);
        } catch (err) {
            setLinkError(err.response?.data?.message || 'Failed to generate link.');
        } finally {
            setIsGeneratingLink(false);
        }
    };
    
    const handleCopyLink = () => {
        navigator.clipboard.writeText(generatedLink).then(() => {
            alert('Payment link copied to clipboard!');
        });
    };

    if (checkingPermissions) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                <Typography>Checking permissions...</Typography>
            </Box>
        );
    }

    if (!canView) {
        return (
            <Alert severity="error" sx={{ mt: 6 }}>
                <strong>Access Denied</strong>
                <br />
                You don't have permission to view Payment settings. Please contact your administrator.
            </Alert>
        );
    }

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;

    const isWithdrawalAmountInvalid = !withdrawalAmount || parseFloat(withdrawalAmount) <= 0 || parseFloat(withdrawalAmount) > parseFloat(balance);
    const isGenerateLinkDisabled = isGeneratingLink || !linkRequest.amount || !linkRequest.title;

    return (
        <Box>
            {/* Balance Card */}
            <Card sx={{ 
                mb: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}>
                <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                        <AccountBalanceWalletIcon color="primary" sx={{ fontSize: 40 }} />
                        <Box>
                            <Typography variant="body2" color="textPrimary" sx={{ fontWeight: 500 }}>
                                Current Available Balance
                            </Typography>
                            <Typography variant="h3" color="primary" sx={{ fontWeight: 700 }}>
                                {balance ? `$${Number(balance).toFixed(2)}` : '$0.00'}
                            </Typography>
                        </Box>
                    </Stack>
                    
                    {error && !openModal && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    
                    {canModify && (
                        <Button 
                            variant="contained" 
                            onClick={handleOpenModal}
                            disabled={!balance || parseFloat(balance) <= 0}
                            size="large"
                            fullWidth
                            sx={{ mt: 2 }}
                        >
                            Withdraw Funds
                        </Button>
                    )}
                </CardContent>
            </Card>

            {/* Collapsible Link Form */}
            <Collapse in={isLinkFormOpen}>
                <Paper sx={{ p: 3, mb: 3, border: `1px solid ${theme.palette.divider}` }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddLinkIcon color="primary" />
                        Create a Specific Link
                    </Typography>
                    <Box component="form" onSubmit={handleGenerateLink}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Payment Type</InputLabel>
                                    <Select
                                        name="payment_type"
                                        value={linkRequest.payment_type}
                                        label="Payment Type"
                                        onChange={handleLinkRequestChange}
                                    >
                                        <MenuItem value="donation">Donation</MenuItem>
                                        <MenuItem value="signup">Signup</MenuItem>
                                        <MenuItem value="others">Others</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField 
                                    fullWidth 
                                    required 
                                    label="Amount (USD)" 
                                    name="amount" 
                                    type="number" 
                                    value={linkRequest.amount} 
                                    onChange={handleLinkRequestChange} 
                                    inputProps={{ step: "0.01", min: "0.50" }} 
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField 
                                    fullWidth 
                                    required 
                                    label="Payment Title" 
                                    name="title" 
                                    value={linkRequest.title} 
                                    onChange={handleLinkRequestChange} 
                                    helperText="Enter a custom title" 
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField 
                                    fullWidth 
                                    label="Description (Optional)" 
                                    name="description" 
                                    multiline 
                                    rows={2} 
                                    value={linkRequest.description} 
                                    onChange={handleLinkRequestChange} 
                                />
                            </Grid>
                        </Grid>
                        <Button 
                            type="submit" 
                            variant="contained" 
                            sx={{ mt: 2 }} 
                            disabled={isGenerateLinkDisabled}
                            fullWidth
                        >
                            {isGeneratingLink ? <CircularProgress size={24} /> : 'Generate Link'}
                        </Button>
                        {linkError && <Alert severity="error" sx={{ mt: 2 }}>{linkError}</Alert>}
                        {generatedLink && (
                            <Paper sx={{ mt: 2, p: 2, bgcolor: alpha(theme.palette.success.main, 0.05) }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Link Generated:</Typography>
                                <Stack direction="row" spacing={1}>
                                    <TextField 
                                        fullWidth 
                                        value={generatedLink} 
                                        InputProps={{ readOnly: true }} 
                                        size="small"
                                    />
                                    <IconButton onClick={handleCopyLink} color="primary">
                                        <ContentCopyIcon />
                                    </IconButton>
                                </Stack>
                            </Paper>
                        )}
                    </Box>
                </Paper>
            </Collapse>

            {/* Plan Ratio Information */}
            <Grid container spacing={3}>
                {/* Current Plan Card */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <TrendingUpIcon color="primary" />
                                <Typography variant="h6" color="textPrimary">Your Current Plan</Typography>
                            </Stack>
                            {isRatiosLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : (
                                planRatios.current_user_plan ? (
                                    <Box>
                                        <Chip 
                                            label={planRatios.current_user_plan.plan_name} 
                                            color="primary" 
                                            sx={{ mb: 2, fontWeight: 600 }} 
                                        />
                                        <Typography variant="body2" color="textPrimary" sx={{ mb: 1 }}>
                                            Your revenue share for each payment is:
                                        </Typography>
                                        <Typography variant="h4" color="secondary.main" sx={{ fontWeight: 700 }}>
                                            {planRatios.current_user_plan.user_share}
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Alert severity="info" icon={<InfoOutlinedIcon />}>
                                        No active plan found.
                                    </Alert>
                                )
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* All Plans Card */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                                <InfoOutlinedIcon color="primary" />
                                <Typography variant="h6" color="textPrimary">Platform Revenue Share by Plan</Typography>
                            </Stack>
                            {isRatiosLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : (
                                <List dense>
                                    {planRatios.all_plan_ratios.map((plan, index) => (
                                        <React.Fragment key={plan.plan_name}>
                                            <ListItem>
                                                <ListItemText 
                                                    primary={
                                                        <Typography variant="subtitle2" fontWeight={600} color="textPrimary">
                                                            {plan.plan_name}
                                                        </Typography>
                                                    }
                                                    secondary={`User receives ${plan.user_share} of each payment`}
                                                />
                                            </ListItem>
                                            {index < planRatios.all_plan_ratios.length - 1 && <Divider component="li" />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Withdrawal Modal */}
            <Modal open={openModal} onClose={() => setOpenModal(false)}>
                <Paper sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: 400 },
                    maxWidth: 400,
                    p: 4,
                    borderRadius: 2
                }}>
                    <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 600 }} color="textPrimary">
                        Initiate Withdrawal
                    </Typography>
                    
                    <TextField
                        fullWidth
                        required
                        label="Amount to Withdraw"
                        type="number"
                        margin="normal"
                        value={withdrawalAmount}
                        onChange={(e) => setWithdrawalAmount(e.target.value)}
                        InputProps={{
                            startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                        }}
                        error={isWithdrawalAmountInvalid && withdrawalAmount !== ''}
                        helperText={
                            parseFloat(withdrawalAmount) > parseFloat(balance)
                                ? "Cannot withdraw more than your available balance."
                                : ""
                        }
                    />

                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                    {isAccountReady && canModify ? (
                        <Button 
                            onClick={handleWithdraw} 
                            disabled={isProcessing || isWithdrawalAmountInvalid} 
                            fullWidth 
                            variant="contained" 
                            sx={{ mt: 3 }}
                            size="large"
                        >
                            {isProcessing ? <CircularProgress size={24} /> : `Confirm and Withdraw $${withdrawalAmount}`}
                        </Button>
                    ) : (
                        <>
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                Your Stripe account is not ready for withdrawals. Please complete your setup.
                            </Alert>
                            <Button 
                                onClick={handleConnectStripe} 
                                disabled={isProcessing} 
                                fullWidth 
                                variant="contained" 
                                color="secondary" 
                                sx={{ mt: 3 }}
                                size="large"
                            >
                                {isProcessing ? <CircularProgress size={24} /> : 'Connect & Set Up Stripe Account'}
                            </Button>
                        </>
                    )}
                </Paper>
            </Modal>
        </Box>
    );
};

export default BalanceDisplay;